import { useState, type FC, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { orderQueryKey, type Order, loadOrder, registrationQueryKey } from '@/api/registration';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import ConfirmationPage from './ConfirmationPage';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';

const PAID = 'Paid';
const APPROVED = 'Approved';
const WAITING_FOR_PAYMENT = 'Waiting for Payment';

const Confirmation: FC<{ isUpsell?: boolean }> = ({ isUpsell }) => {
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const { data: eventSetting } = useEventSettingsQuery(eventId);
  const [searchParams] = useSearchParams();
  const orderHashParam = searchParams.get('orderHash');
  const registrationQuery = useRegistrationQuery(eventId);
  const queryClient = useQueryClient();
  const [orderHash, setOrderHash] = useState<string | null>(null);

  const { data, isLoading, error: fetchError } = useQuery<ApiResponseSingle<Order>, Error>({
    queryKey: [orderQueryKey, { orderHash }],
    queryFn: async () => {
      if (!orderHash) {
        throw new Error('orderHash is missing, cannot complete query');
      }
      return await loadOrder(createTenantApiRequest)(orderHash);
    },
    enabled: !!orderHash && !isApiPreparing
  });

  useEffect(() => {
    void registrationQuery.refetch();
  }, []);

  useEffect(() => {
    const _orderHash = isUpsell ? registrationQuery?.data?.orderHashUpsell : registrationQuery?.data?.orderHash;

    if (_orderHash) {
      void queryClient.invalidateQueries([orderQueryKey, { orderHash }]);
      setOrderHash(_orderHash);
      void queryClient.invalidateQueries([registrationQueryKey, { eventId }]);
    }
  }, [eventId, isUpsell, orderHash, orderHashParam, queryClient, registrationQuery?.data?.orderHash, registrationQuery?.data?.orderHashUpsell]);

  const isInvoice = data?.item?.paymentOption === 'invoice';
  const statusesToCheck = isInvoice ? [WAITING_FOR_PAYMENT, PAID, APPROVED] : [APPROVED, PAID];

  return (
    <ConfirmationPage data={data} isLoading={isLoading} fetchError={fetchError} statusesToCheck={statusesToCheck} confirmationMetadata={eventSetting?.pageMetadata?.confirmation} />
  );
};

export default Confirmation;
